import { toast } from "@carbon6/c6-frontend-design";
import {
  deleteTrainingSessions,
  restoreTrainingSessions,
  type SessionBulkSummaryView,
  type SessionBulkView,
} from "@/api";
import { isHttpClientError } from "@/http";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";

// 삭제를 되돌릴 수 있는 기간. 이 기간이 지나면 상류 `session-archive-cleanup` CronJob 이 archive
// 세션의 MLflow run·GCS 아티팩트·DB 행을 지운다(training-infra
// `deploy/manifests/session-archive-cleanup-cronjob.yaml` 의 SESSION_ARCHIVE_RETENTION_DAYS).
// 상류가 이 값을 응답으로 보내지 않아 화면이 운영값을 복사한다. 매니페스트가 바뀌면 함께 고친다.
const DELETE_RETENTION_DAYS = 7;

// 삭제·복원. 목록 행 ⋯ 메뉴와 선택 도구 바가 같은 문구와 절차를 쓰므로 여기 모은다
// (시작 액션 3종의 sessionStartActions 와 같은 구조).
//
// 화면 표기는 "삭제"지만 상류 개념은 archive(유예 삭제)다. 진행 중인 학습을 취소하고, 유예
// 기간이 지나면 산출물까지 지운다. 확인 문구는 이 두 가지와 유예 기간을 모두 알린다.
export type SessionDeleteKind = "delete" | "restore";

export const sessionDeleteActionText: Record<SessionDeleteKind, { label: string; hint: string }> = {
  delete: { label: "삭제", hint: "목록에서 감추고 일정 기간 뒤 영구 삭제합니다." },
  restore: { label: "복원", hint: "삭제 예정을 해제해 목록으로 되돌립니다." },
};

// 삭제 확인 모달 문구. 대상이 하나면 개수를 표시하지 않는다. 무엇을 골랐는지 행에서 이미 보인다.
export function deleteConfirmText(count: number): { title: string; caption: string } {
  return {
    title:
      count > 1 ? `Training Session ${count}개를 삭제합니다.` : "Training Session을 삭제합니다.",
    caption:
      `삭제하면 목록에서 감춰지고 ${DELETE_RETENTION_DAYS}일 뒤 Checkpoint·산출물과 함께 영구 삭제됩니다. ` +
      "진행 중인 Training이면 즉시 취소됩니다. 그 전까지는 [삭제 예정 포함] 을 켜서 복원할 수 있습니다.",
  };
}

// 상류 bulk 는 행마다 ok/skipped/error 를 반환한다. 모두 성공이 아니면 무엇이 남았는지 알린다.
// skipped 는 실패가 아니다. 이미 그 상태였다는 뜻이라 다시 시도할 것이 없다.
function summaryMessage(kind: SessionDeleteKind, summary: SessionBulkSummaryView): string {
  const noun = sessionDeleteActionText[kind].label;
  if (summary.skipped === 0 && summary.error === 0) {
    return summary.ok > 1
      ? `${summary.ok}개 Session을 ${noun}했습니다.`
      : `Session을 ${noun}했습니다.`;
  }
  const parts = [`${summary.ok}개 ${noun}`];
  if (summary.skipped > 0) {
    parts.push(`${summary.skipped}개 건너뜀`);
  }
  if (summary.error > 0) {
    parts.push(`${summary.error}개 실패`);
  }
  return parts.join(" · ");
}

export type SessionDeleteInput = {
  sessionIds: readonly string[];
};

// 삭제·복원을 실행하고 결과 요약 토스트를 표시한다. 전송이 실패하면 호출부가 문구로 바꾸도록
// 그대로 던진다(행 메뉴는 인라인으로, 선택 바는 토스트로 받는다).
export async function applySessionDelete(
  kind: SessionDeleteKind,
  { sessionIds }: SessionDeleteInput,
): Promise<SessionBulkView> {
  const view =
    kind === "delete"
      ? await deleteTrainingSessions(sessionIds)
      : await restoreTrainingSessions(sessionIds);
  const message = summaryMessage(kind, view.summary);
  if (view.summary.error > 0) {
    toast.warning(message);
  } else {
    toast.success(message);
  }
  return view;
}

// 삭제·복원 실패 문구. 상류와 BE 는 이 액션에 선점자 게이트를 두지 않으므로 403 경우가 없다.
// 실제로 처리할 경우는 404(그 사이 사라졌거나 스코프 밖) 하나다.
export function resolveSessionDeleteErrorMessage(kind: SessionDeleteKind, error: unknown): string {
  if (isHttpClientError(error) && error.status === 404) {
    const noun = sessionDeleteActionText[kind].label;
    return `${noun}할 Session을 찾지 못했습니다. 목록을 새로고침해 주세요.`;
  }
  return resolveLoadErrorMessage(error);
}
